import { BaseApiClient } from "../api/baseApiClient.service";
import type { JWTPayload } from "../auth/auth.types";

export type PodWorkspace = {
	id: string;
	title: string;
	visibility: "public" | "private";
	createdAt: Date;
	createdBy: JWTPayload;
};

export type Visibility = "public" | "private";

export type RecentlyPlayedWorkspace = PodWorkspace & {
	lastListenedAt: Date;
	totalListens: number;
	podcasts: Array<{
		id: string;
		title: string;
		status: string;
		audioUrl: string | null;
		scriptUrl: string | null;
		createdAt: Date;
		updatedAt: Date;
		podWorkspaceId: string;
		progress: {
			currentTime: number;
			duration: number | null;
			completed: boolean;
			lastListenedAt: Date;
			progressPercentage: number;
		} | null;
	}>;
};

export interface CreatePodWorkspaceParams {
	title: string;
}

/**
 * This service is used to interact with the pod workspace API.
 */
export class PodWorkspaceService {
	static path = "pod-workspace";

	/**
	 * Get all workspaces for the current user
	 * @returns The workspaces for the current user
	 */
	static async getAllWorkspacesForUser(): Promise<PodWorkspace[]> {
		return BaseApiClient.getInstance().get<PodWorkspace[]>(
			`${PodWorkspaceService.path}/all`,
		);
	}

	/**
	 * Create a new pod workspace
	 */
	static async createPodWorkspace(
		params: CreatePodWorkspaceParams,
	): Promise<PodWorkspace> {
		return BaseApiClient.getInstance().post<
			PodWorkspace,
			CreatePodWorkspaceParams
		>(`${PodWorkspaceService.path}/create`, params);
	}

	/**
	 * Get recently played workspaces based on podcast playback activity and recent creation
	 * Returns workspaces ordered by most recent activity (either listen activity or creation time)
	 * This includes both listened-to podcasts AND newly created podcasts that haven't been played yet
	 * @param limit - Maximum number of workspaces to return (default: 10, max: 50)
	 * @returns Recently played workspaces with listen metadata
	 */
	static async getRecentlyPlayedWorkspaces(
		limit = 10,
	): Promise<RecentlyPlayedWorkspace[]> {
		const queryParams = new URLSearchParams({
			limit: limit.toString(),
		});
		return BaseApiClient.getInstance().get<RecentlyPlayedWorkspace[]>(
			`${PodWorkspaceService.path}/recently-played?${queryParams}`,
		);
	}

	/**
	 * Update a pod workspace
	 * @param workspaceId - The ID of the workspace to update
	 * @param data - The data to update
	 * @returns The updated workspace
	 */
	static async updatePodWorkspace(
		workspaceId: string,
		data: { title?: string; visibility?: Visibility },
	): Promise<PodWorkspace> {
		return BaseApiClient.getInstance().patch<
			PodWorkspace,
			{ title?: string; visibility?: Visibility }
		>(`${PodWorkspaceService.path}/${workspaceId}`, data);
	}

	/**
	 * Delete a pod workspace
	 * @param workspaceId - The ID of the workspace to delete
	 * @returns The deleted workspace
	 */
	static async deletePodWorkspace(workspaceId: string): Promise<PodWorkspace> {
		return BaseApiClient.getInstance().delete<PodWorkspace>(
			`${PodWorkspaceService.path}/${workspaceId}`,
		);
	}
}
